import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { config } from "@/config";

type ProtectedRouteProps = {
  children: ReactNode;
  role?: string;
  requireAuth?: boolean;
  requirePlatformRole?: boolean;
};

const ProtectedRoute = ({ children, role, requireAuth = true, requirePlatformRole = false }: ProtectedRouteProps) => {
  const { user, loading, isAuthenticated, userRoles } = useAuth();
  const location = useLocation();
  const enforceLogin = config.ENFORCE_LOGIN;

  useEffect(() => {
    console.log("ProtectedRoute checking access for:", location.pathname);
    console.log("Auth state:", { isAuthenticated, loading, userRole: user?.role });
    console.log("Login enforcement:", enforceLogin ? "Enabled" : "Disabled");
    console.log("Platform role required:", requirePlatformRole);
    console.log("User platform roles:", userRoles?.platform || []);
    console.log("Full location object:", {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state
    });
  }, [location.pathname, isAuthenticated, loading, user, enforceLogin, requirePlatformRole, userRoles]);

  // Public routes that should be accessible without authentication
  const publicRoutes = ['/login', '/register', '/forgot-password', '/auth/login', '/auth/forgot-password', '/auth/set-password'];
  
  // Check if current path is a public route
  const isPublicRoute = publicRoutes.some(route => {
    console.log(`Checking route: ${location.pathname} against pattern: ${route}`);
    
    // Exact match or starts with for set-password
    if (location.pathname === route || 
        (route === '/auth/set-password' && location.pathname.startsWith('/auth/set-password'))) {
      console.log("Route match found for:", route);
      return true;
    }
    
    return false;
  });
  
  // Special handling for password reset links from Supabase
  // Check both direct links and links with tokens
  const isPasswordResetWithToken = 
    // Check for the set-password path
    location.pathname.startsWith('/auth/set-password') || 
    // Check for direct Supabase auth verify URLs
    location.pathname.includes('/auth/v1/verify') ||
    // Check for token in search params
    location.search.includes('token=') || 
    location.search.includes('access_token=') ||
    // Check for token in hash params
    location.hash.includes('access_token=') || 
    location.hash.includes('type=recovery');

  // If the URL has a hash with tokens, this is likely a password reset flow
  // Redirect to the set-password page
  if (location.hash && 
      location.hash.includes('access_token=') && 
      location.hash.includes('type=recovery') && 
      location.pathname !== '/auth/set-password') {
    console.log("Detected recovery access_token in hash, redirecting to set-password");
    return <Navigate to="/auth/set-password" state={{ from: location, preserveHash: true }} replace />;
  }

  // Direct password reset url handling
  if (isPasswordResetWithToken) {
    console.log("Password reset link detected, allowing access");
    return <>{children}</>;
  }
  
  if (loading) {
    console.log("Auth is still loading");
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
      </div>
    );
  }

  // Allow access to public routes without authentication
  if (isPublicRoute) {
    console.log("Allowing access to public route:", location.pathname);
    return <>{children}</>;
  }
  
  // If login enforcement is disabled, bypass authentication check
  if (!enforceLogin) {
    console.log("Login enforcement disabled, allowing access to:", location.pathname);
    return <>{children}</>;
  }
  
  // Check if authentication is required and user is not authenticated
  if (requireAuth && !isAuthenticated) {
    console.log("User not authenticated, redirecting to /login from:", location.pathname);
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Check if platform role is required
  if (requirePlatformRole && (!userRoles || userRoles.platform.length === 0)) {
    console.log("Platform role required but user doesn't have platform roles, redirecting from:", location.pathname);
    return <Navigate to="/backoffice/dashboard" replace />;
  }

  // If role is specified, check if user has that role
  if (role && user?.role !== role) {
    console.log(`User doesn't have required role: ${role}, redirecting from:`, location.pathname);
    // Redirect to appropriate dashboard based on role
    if (user?.role === "buyer") {
      return <Navigate to="/buyer/dashboard" replace />;
    } else if (["admin", "document_manager", "finance_manager", "support"].includes(user?.role || "")) {
      return <Navigate to="/backoffice/dashboard" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  console.log("Access granted to:", location.pathname);
  return <>{children}</>;
};

export default ProtectedRoute;
